import React, { Component } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ScopeService from '../../../services/scopeService';
import '../../../assets/css/dealerSignup.css';

/**
 * DealerWelcome Component
 *
 * First-time landing page for new dealer users after they complete signup
 * and log in for the first time. Shows onboarding information and next steps.
 */
class DealerWelcome extends Component {
  constructor(props) {
    super(props);

    this.state = {
      user: null,
      company: null,
      isFirstLogin: true
    };
  }

  componentDidMount() {
    // Check if user is authenticated and load their data
    const user = ScopeService.getUser();
    const company = ScopeService.getCompany();

    if (user && company) {
      this.setState({ user, company });

      // Check if this is truly a first login by checking if a "welcomed" flag exists
      const hasBeenWelcomed = localStorage.getItem('dealer_welcomed');
      if (hasBeenWelcomed) {
        this.setState({ isFirstLogin: false });
      }
    }
  }

  handleGetStarted = () => {
    // Mark user as welcomed
    localStorage.setItem('dealer_welcomed', 'true');
    // Redirect to requestimates
    window.location.href = '/requestimates';
  };

  render() {
    const { user, company } = this.state;

    // If not authenticated, show login prompt
    if (!user || !company) {
      return (
        <Container className="mt-5">
          <Row className="justify-content-center">
            <Col md={6} className="text-center">
              <h3>Please log in to continue</h3>
              <p className="text-muted">You must be logged in to access this page.</p>
              <a href="/" className="btn btn-primary">
                Go to Login
              </a>
            </Col>
          </Row>
        </Container>
      );
    }

    return (
      <Container className="dealer-welcome-container mt-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            {/* Welcome Header */}
            <div className="text-center mb-5">
              <h1 className="display-4">Welcome!</h1>
              <p className="lead text-muted">
                Your account for <strong>{company.dealer_name}</strong> is ready to go.
              </p>
            </div>

            {/* Getting Started Cards */}
            <Row className="mb-4">
              <Col md={4} className="mb-3">
                <Card className="h-100 shadow-sm feature-card">
                  <Card.Body className="text-center">
                    <div className="feature-icon mb-3">
                      <i className="fa-sharp fa-solid fa-file-invoice text-primary" style={{ fontSize: '2.5rem' }}></i>
                    </div>
                    <Card.Title>Create Estimates</Card.Title>
                    <Card.Text>
                      Submit service requests (Requestimates) to nearby specialists for automotive services like tint, PPF, and more.
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4} className="mb-3">
                <Card className="h-100 shadow-sm feature-card">
                  <Card.Body className="text-center">
                    <div className="feature-icon mb-3">
                      <i className="fa-sharp fa-solid fa-users text-success" style={{ fontSize: '2.5rem' }}></i>
                    </div>
                    <Card.Title>Find Specialists</Card.Title>
                    <Card.Text>
                      Browse and connect with verified automotive service specialists in your area based on proximity and ratings.
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4} className="mb-3">
                <Card className="h-100 shadow-sm feature-card">
                  <Card.Body className="text-center">
                    <div className="feature-icon mb-3">
                      <i className="fa-sharp fa-solid fa-calendar-check text-info" style={{ fontSize: '2.5rem' }}></i>
                    </div>
                    <Card.Title>Manage Jobs</Card.Title>
                    <Card.Text>
                      Track your requests, schedule appointments, and manage completed work all in one place.
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Next Steps */}
            <Card className="shadow-sm mb-4">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fa-sharp fa-solid fa-list-check me-2"></i>
                  Next Steps
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="next-steps-list">
                  <div className="step-item mb-3 pb-3 border-bottom">
                    <div className="d-flex align-items-start">
                      <div className="step-number me-3">
                        <span className="badge bg-primary rounded-circle" style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</span>
                      </div>
                      <div>
                        <h6 className="mb-1">Complete Your Profile</h6>
                        <p className="text-muted mb-0">
                          Add additional team members and configure your dealership settings.
                        </p>
                        <Link to="/profile" className="btn btn-sm btn-outline-primary mt-2">
                          Go to Profile
                        </Link>
                      </div>
                    </div>
                  </div>

                  <div className="step-item mb-3 pb-3 border-bottom">
                    <div className="d-flex align-items-start">
                      <div className="step-number me-3">
                        <span className="badge bg-primary rounded-circle" style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>2</span>
                      </div>
                      <div>
                        <h6 className="mb-1">Browse Available Specialists</h6>
                        <p className="text-muted mb-0">
                          View specialists in your area and see which services they offer. We've already calculated distances for you.
                        </p>
                        <Link to="/services" className="btn btn-sm btn-outline-primary mt-2">
                          View Specialists
                        </Link>
                      </div>
                    </div>
                  </div>

                  <div className="step-item mb-3 pb-3">
                    <div className="d-flex align-items-start">
                      <div className="step-number me-3">
                        <span className="badge bg-primary rounded-circle" style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</span>
                      </div>
                      <div>
                        <h6 className="mb-1">Create Your First Requestimate</h6>
                        <p className="text-muted mb-0">
                          Ready to get started? Create your first service request and connect with specialists.
                        </p>
                        <Link to="/newrequestimates" className="btn btn-sm btn-primary mt-2">
                          Create Requestimate
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Help and Resources */}
            <Card className="shadow-sm mb-4">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fa-sharp fa-solid fa-question-circle me-2"></i>
                  Need Help?
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6} className="mb-3 mb-md-0">
                    <h6>Contact Support</h6>
                    <p className="text-muted mb-2">
                      Our team is here to help you get started.
                    </p>
                    <p className="mb-0">
                      <i className="fa-sharp fa-solid fa-envelope me-2"></i>
                      <a href="mailto:support@jobvious.com">support@jobvious.com</a>
                    </p>
                  </Col>
                  <Col md={6}>
                    <h6>Quick Tips</h6>
                    <ul className="text-muted small mb-0">
                      <li>Requestimates are estimates you request from specialists</li>
                      <li>You can manage multiple users from your profile</li>
                      <li>Specialists are matched based on proximity and services</li>
                      <li>Track all your jobs in the calendar view</li>
                    </ul>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* CTA Button */}
            <div className="text-center mb-5">
              <Button
                variant="primary"
                size="lg"
                onClick={this.handleGetStarted}
                className="px-5"
              >
                Get Started <i className="fa-sharp fa-solid fa-arrow-right ms-2"></i>
              </Button>
              <p className="text-muted mt-3 small">
                You can always access these resources from your dashboard
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default DealerWelcome;
